import { useEffect, useState } from 'react';
import { getProposal } from '../../services/agent';
import type { ProposalDetail as P } from '../../types/agent';
import { ContextPanel } from './ContextPanel';
import { ConversationPanel } from './ConversationPanel';
import { ActionCard } from './ActionCard';
import { ApprovalBar } from './ApprovalBar';

export function ProposalDetail({ proposalId }: { proposalId: string }) {
  const [p, setP] = useState<P | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, any>>({});

  const reload = async () => {
    const data = await getProposal(proposalId);
    setP(data);
    setSelected(new Set(
      data.actions.filter(a => a.status === 'pending').map(a => a.id)
    ));
  };

  useEffect(() => { reload(); }, [proposalId]);

  if (!p) return <div className="p-4">Loading...</div>;

  return (
    <div className="pb-24">
      <div className="space-y-2 mb-6">
        <h2 className="text-xl font-semibold">
          {p.athlete.name} - {p.team_name}
        </h2>
        <p className="text-sm">{p.summary}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <ContextPanel p={p} />
          <ConversationPanel p={p} />
        </div>
        <section className="border border-neutral-300 dark:border-neutral-700 p-4">
          <h3 className="font-semibold mb-2">Proposed actions</h3>
          <div className="space-y-2">
            {p.actions.map(a => (
              <ActionCard
                key={a.id}
                action={a}
                selected={selected.has(a.id)}
                onToggle={(id, next) => {
                  const s = new Set(selected);
                  if (next) s.add(id); else s.delete(id);
                  setSelected(s);
                }}
                onEdit={(id, payload) =>
                  setEdits(prev => ({ ...prev, [id]: payload }))
                }
              />
            ))}
          </div>
        </section>
      </div>
      <ApprovalBar
        proposalId={p.id}
        selectedIds={Array.from(selected)}
        payloadEdits={edits}
        onDone={reload}
      />
    </div>
  );
}
